import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, Loader2, Lock, LockKeyhole, RefreshCw, Search, ShieldAlert, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import './PasswordVault.css';

const PAGE_SIZE = 10;

const generatePassword = () => {
    const pick = chars => chars[Math.floor(Math.random() * chars.length)];
    const all = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const chars = [pick('ABCDEFGHJKMNPQRSTUVWXYZ'), pick('23456789'), pick('@#$%&*!'), ...Array.from({ length: 7 }, () => pick(all))];
    return chars.sort(() => Math.random() - 0.5).join('');
};

// password box with a show / hide eye
const SecretInput = ({ className = '', ...props }) => {
    const [visible, setVisible] = useState(false);
    return (
        <span className={`vault-eye ${className}`}>
            <input {...props} type={visible ? 'text' : 'password'} />
            <button type="button" tabIndex={-1} aria-label={visible ? 'Hide' : 'Show'} title={visible ? 'Hide' : 'Show'} onClick={() => setVisible(v => !v)}>
                {visible ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
        </span>
    );
};

const PasswordVault = () => {
    const [status, setStatus] = useState(null); // { codeSet, vaultEnabled }
    const [token, setToken] = useState('');
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [accounts, setAccounts] = useState([]);
    const [tab, setTab] = useState('member');
    const [query, setQuery] = useState('');
    const [shown, setShown] = useState({});
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ code: '', adminPassword: '', currentCode: '' });
    const [changingCode, setChangingCode] = useState(false);
    const timer = useRef(null);

    const loadStatus = useCallback(() => API.get('/admin/password-vault/status').then(r => setStatus(r.data)).catch(() => toast.error('Could not load vault status')), []);
    useEffect(() => { loadStatus(); return () => clearInterval(timer.current); }, [loadStatus]);

    const lock = useCallback(() => {
        clearInterval(timer.current);
        setToken(''); setAccounts([]); setShown({}); setTarget(null); setSecondsLeft(0);
    }, []);

    const authHeaders = tk => ({ headers: { 'x-vault-token': tk } });

    const loadAccounts = useCallback(async tk => {
        setLoading(true);
        try {
            const res = await API.get('/admin/password-vault/accounts', authHeaders(tk));
            setAccounts(res.data.accounts || []);
        } catch (err) {
            if (err.response?.data?.code === 'VAULT_LOCKED') lock();
            toast.error(err.response?.data?.message || 'Could not load accounts');
        } finally {
            setLoading(false);
        }
    }, [lock]);

    const startCountdown = seconds => {
        clearInterval(timer.current);
        setSecondsLeft(seconds);
        timer.current = setInterval(() => {
            setSecondsLeft(s => {
                if (s <= 1) { lock(); toast('Vault locked automatically', { icon: '🔒' }); return 0; }
                return s - 1;
            });
        }, 1000);
    };

    const unlock = async event => {
        event.preventDefault();
        try {
            const res = await API.post('/admin/password-vault/unlock', { code: form.code });
            setForm(f => ({ ...f, code: '' }));
            setToken(res.data.token);
            startCountdown(res.data.expiresInSeconds || 300);
            loadAccounts(res.data.token);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not unlock');
        }
    };

    const saveCode = async event => {
        event.preventDefault();
        try {
            await API.post('/admin/password-vault/code', { code: form.code, adminPassword: form.adminPassword, currentCode: form.currentCode });
            toast.success('Secret code saved');
            setForm({ code: '', adminPassword: '', currentCode: '' });
            setChangingCode(false);
            loadStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not save code');
        }
    };

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return accounts.filter(a => a.role === tab && (!q || [a.name, a.email, a.mobile].some(v => String(v || '').toLowerCase().includes(q))));
    }, [accounts, tab, query]);

    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [tab, query]);
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const copy = async text => {
        try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Copy failed'); }
    };

    const save = async event => {
        event.preventDefault();
        setSaving(true);
        try {
            const res = await API.put(`/admin/password-vault/${target._id}`, { newPassword }, authHeaders(token));
            setAccounts(list => list.map(a => (a._id === target._id ? { ...a, password: res.data.password } : a)));
            toast.success('Password updated');
            setTarget(null);
        } catch (err) {
            if (err.response?.data?.code === 'VAULT_LOCKED') lock();
            toast.error(err.response?.data?.message || 'Could not update password');
        } finally {
            setSaving(false);
        }
    };

    const field = key => ({ value: form[key], onChange: e => setForm(f => ({ ...f, [key]: e.target.value })) });
    const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

    return (
        <div className="vault-layout">
            <Sidebar />
            <main className="admin-content vault-content">
                <Navbar />
                <div className="vault-page">
                    <header className="vault-heading">
                        <div className="vault-eyebrow"><LockKeyhole size={14} /> Users &amp; Access</div>
                        <h1>Password vault</h1>
                        <p>View and update member and service provider passwords. Protected by your secret code; every unlock and change is logged.</p>
                    </header>

                    {!status ? (
                        <div className="vault-card vault-center"><Loader2 size={22} className="vault-spin" /></div>
                    ) : !status.vaultEnabled ? (
                        <div className="vault-card vault-warn">
                            <ShieldAlert size={20} />
                            <div>
                                <b>Vault is not enabled on the server.</b>
                                <p>Add a <code>PASSWORD_VAULT_KEY</code> (64 hex characters, or a 32-byte base64 key) to the server environment and restart it. Passwords are only viewable for accounts whose password is set or changed after that.</p>
                            </div>
                        </div>
                    ) : !status.codeSet || changingCode ? (
                        <form className="vault-card vault-form" onSubmit={saveCode}>
                            <h2>{status.codeSet ? 'Change secret code' : 'Create your secret code'}</h2>
                            <p>You will enter this code each time you open the vault. Keep it different from your login password.</p>
                            {status.codeSet && <label><span>Current secret code</span><SecretInput autoComplete="off" required {...field('currentCode')} /></label>}
                            <label><span>New secret code (6+ characters)</span><SecretInput autoComplete="off" minLength={6} required {...field('code')} /></label>
                            <label><span>Your account password (to confirm it's you)</span><SecretInput autoComplete="current-password" required {...field('adminPassword')} /></label>
                            <div className="vault-actions">
                                <button type="submit" className="vault-primary"><KeyRound size={16} /> Save code</button>
                                {changingCode && <button type="button" className="vault-ghost" onClick={() => setChangingCode(false)}>Cancel</button>}
                            </div>
                        </form>
                    ) : !token ? (
                        <form className="vault-card vault-form vault-unlock" onSubmit={unlock}>
                            <Lock size={28} />
                            <h2>Vault locked</h2>
                            <p>Enter your secret code to view passwords. It re-locks after 5 minutes.</p>
                            <SecretInput placeholder="Secret code" autoComplete="off" autoFocus required {...field('code')} />
                            <button type="submit" className="vault-primary"><LockKeyhole size={16} /> Unlock</button>
                            <button type="button" className="vault-link" onClick={() => setChangingCode(true)}>Change secret code</button>
                        </form>
                    ) : (
                        <>
                            <div className="vault-bar">
                                <div className="vault-tabs">
                                    <button type="button" className={tab === 'member' ? 'is-active' : ''} onClick={() => setTab('member')}>Members</button>
                                    <button type="button" className={tab === 'service' ? 'is-active' : ''} onClick={() => setTab('service')}>Service providers</button>
                                </div>
                                <label className="vault-search"><Search size={16} /><input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, email or mobile" /></label>
                                <div className="vault-timer">Locks in {mmss}</div>
                                <button type="button" className="vault-ghost" onClick={lock}><Lock size={15} /> Lock now</button>
                            </div>

                            <div className="vault-table-wrap">
                                {loading ? (
                                    <div className="vault-center"><Loader2 size={22} className="vault-spin" /></div>
                                ) : rows.length === 0 ? (
                                    <div className="vault-center">No accounts found</div>
                                ) : (
                                    <table className="vault-table">
                                        <thead><tr><th>Name</th><th>Mobile</th><th>Email</th><th>Password</th><th /></tr></thead>
                                        <tbody>
                                            {pageRows.map(a => (
                                                <tr key={a._id}>
                                                    <td>{a.name || '—'}</td>
                                                    <td>{a.mobile || '—'}</td>
                                                    <td>{a.email || '—'}</td>
                                                    <td>
                                                        {a.password ? (
                                                            <span className="vault-pw">
                                                                <code>{shown[a._id] ? a.password : '••••••••'}</code>
                                                                <button type="button" onClick={() => setShown(s => ({ ...s, [a._id]: !s[a._id] }))} aria-label="Show or hide">{shown[a._id] ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                                                                <button type="button" onClick={() => copy(a.password)} aria-label="Copy"><Copy size={15} /></button>
                                                            </span>
                                                        ) : <span className="vault-na">Not available — set a new password</span>}
                                                    </td>
                                                    <td><button type="button" className="vault-reset" onClick={() => { setTarget(a); setNewPassword(generatePassword()); }}>Update</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {rows.length > PAGE_SIZE && (
                                <div className="vault-pager">
                                    <span>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, rows.length)} of {rows.length}</span>
                                    <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                                    <span>Page {currentPage} / {totalPages}</span>
                                    <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {target && (
                    <div className="vault-overlay" onClick={() => !saving && setTarget(null)}>
                        <form className="vault-modal" onClick={e => e.stopPropagation()} onSubmit={save}>
                            <button type="button" className="vault-close" onClick={() => setTarget(null)} aria-label="Close"><X size={18} /></button>
                            <h2>Update password</h2>
                            <p>{target.name || target.mobile} · {target.mobile}</p>
                            <div className="vault-input-row">
                                <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="off" required />
                                <button type="button" onClick={() => setNewPassword(generatePassword())} aria-label="Generate"><RefreshCw size={16} /></button>
                            </div>
                            <small>At least 6 characters with an uppercase letter, a number and a symbol.</small>
                            <button type="submit" className="vault-primary" disabled={saving}>{saving ? <Loader2 size={16} className="vault-spin" /> : <KeyRound size={16} />} Update password</button>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PasswordVault;
